import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { STORE_KINDS, type StockItemView, type StoreKind } from '@wodi/shared';
import {
  Button,
  Cell,
  Checkbox,
  Column,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Select,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextField,
  Section,
} from '@wodi/ui';
import { useState } from 'react';

import { can, sessionQuery } from '../api/session.ts';
import {
  adminStoresQuery,
  createStore,
  createSupplier,
  searchSuppliers,
  setStockDetails,
  updateStore,
  updateSupplier,
} from '../api/stock.ts';
import { useOnline } from '../hooks/use-online.ts';
import { errorText } from '../stock/common.tsx';
import { readQuantity } from '../stock/format.ts';
import { StockItemPicker } from '../stock/item-picker.tsx';
import styles from '../stock/stock.module.css';
import { toasts } from '../toasts.ts';
import { SettingsPage } from './settings-nav.tsx';

const KIND_LABELS: Record<StoreKind, string> = {
  main: 'Main store',
  pharmacy: 'Pharmacy',
  lab: 'Lab',
  other: 'Other',
};

/**
 * Settings > Stores and suppliers (plan §5.8): the stores at this facility, the suppliers
 * the organisation buys from, and how each catalogue item is counted in stock.
 */
export function StoresSettingsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  if (session === null || !can(session, 'stock.manage'))
    return (
      <>
        <PageHeader title="Stores and suppliers" />
        <Notice tone="info">
          You don't have permission to set up stores. Ask an administrator if you need it.
        </Notice>
      </>
    );
  return (
    <SettingsPage title="Stores and suppliers">
      <Stores facilityName={session.facility.name} />
      <Suppliers />
      <ItemSetup />
    </SettingsPage>
  );
}

function Stores({ facilityName }: { facilityName: string }) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const stores = useQuery(adminStoresQuery);
  const [name, setName] = useState('');
  const [kind, setKind] = useState<StoreKind | null>(null);
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: adminStoresQuery.queryKey });
    await queryClient.invalidateQueries({ queryKey: ['stores'] });
  };
  const add = useMutation({
    mutationFn: () => createStore({ name: name.trim(), kind: kind ?? 'other' }),
    onSuccess: async (store) => {
      await refresh();
      toasts.show({ title: 'Store added', description: store.name });
      setName('');
      setKind(null);
    },
  });
  const toggle = useMutation({
    mutationFn: (s: { id: string; status: 'active' | 'inactive' }) =>
      updateStore(s.id, { status: s.status === 'active' ? 'inactive' : 'active' }),
    onSuccess: refresh,
  });
  return (
    <section className={styles.section} aria-labelledby="stores-heading">
      <h2 id="stores-heading" className={styles.sectionHeading}>
        Stores at {facilityName}
      </h2>
      {stores.isPending ? <LoadingBlock label="Loading stores" rows={2} /> : null}
      {stores.isError ? (
        <Notice tone="error">{errorText(stores.error, "Stores couldn't be loaded.")}</Notice>
      ) : null}
      {stores.isSuccess ? (
        <Table flat aria-label="Stores" dense>
          <TableHeader>
            <Column isRowHeader>Name</Column>
            <Column>Kind</Column>
            <Column>Status</Column>
            <Column>Actions</Column>
          </TableHeader>
          <TableBody
            items={stores.data}
            renderEmptyState={() => 'No stores yet. Add the main store first.'}
          >
            {(s) => (
              <Row id={s.id}>
                <Cell>{s.name}</Cell>
                <Cell>{KIND_LABELS[s.kind]}</Cell>
                <Cell>
                  <Tag state={s.status === 'active' ? 'active' : 'neutral'}>
                    {s.status === 'active' ? 'Active' : 'Inactive'}
                  </Tag>
                </Cell>
                <Cell>
                  <Button
                    dense
                    variant="quiet"
                    isDisabled={!online}
                    onPress={() => {
                      toggle.mutate(s);
                    }}
                  >
                    {s.status === 'active' ? 'Switch off' : 'Switch on'}
                  </Button>
                </Cell>
              </Row>
            )}
          </TableBody>
        </Table>
      ) : null}
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim() !== '' && kind !== null) add.mutate();
        }}
      >
        <div className={styles.row}>
          <TextField label="New store name" value={name} onChange={setName} />
          <Select
            label="Kind"
            placeholder="Choose a kind"
            options={STORE_KINDS.map((k) => ({ id: k, label: KIND_LABELS[k] }))}
            value={kind}
            onChange={(v) => {
              setKind(v as StoreKind);
            }}
          />
        </div>
        {add.error === null ? null : (
          <Notice tone="error">{errorText(add.error, "The store wasn't added. Try again.")}</Notice>
        )}
        {toggle.error === null ? null : (
          <Notice tone="error">{errorText(toggle.error, "That change wasn't saved.")}</Notice>
        )}
        <div className={styles.actions}>
          <Button
            type="submit"
            isDisabled={!online || name.trim() === '' || kind === null}
            isPending={add.isPending}
            pendingLabel="Adding…"
          >
            Add store
          </Button>
        </div>
      </form>
    </section>
  );
}

function Suppliers() {
  const online = useOnline();
  const queryClient = useQueryClient();
  const suppliers = useQuery({
    queryKey: ['suppliers', 'all'],
    queryFn: () => searchSuppliers('', true),
  });
  const [name, setName] = useState('');
  const [kraPin, setKraPin] = useState('');
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['suppliers'] });
  const add = useMutation({
    mutationFn: () => createSupplier({ name: name.trim(), kraPin: kraPin.trim() }),
    onSuccess: async (s) => {
      await refresh();
      toasts.show({ title: 'Supplier added', description: s.name });
      setName('');
      setKraPin('');
    },
  });
  const toggle = useMutation({
    mutationFn: (s: { id: string; status: 'active' | 'inactive' }) =>
      updateSupplier(s.id, { status: s.status === 'active' ? 'inactive' : 'active' }),
    onSuccess: refresh,
  });
  return (
    <Section title="Suppliers">
      {suppliers.isPending ? <LoadingBlock label="Loading suppliers" rows={2} /> : null}
      {suppliers.isError ? (
        <Notice tone="error">{errorText(suppliers.error, "Suppliers couldn't be loaded.")}</Notice>
      ) : null}
      {suppliers.isSuccess ? (
        <Table flat aria-label="Suppliers" dense>
          <TableHeader>
            <Column isRowHeader>Name</Column>
            <Column>KRA PIN</Column>
            <Column>Status</Column>
            <Column>Actions</Column>
          </TableHeader>
          <TableBody items={suppliers.data} renderEmptyState={() => 'No suppliers yet.'}>
            {(s) => (
              <Row id={s.id}>
                <Cell>{s.name}</Cell>
                <Cell>
                  <span className="wodi-id">{s.kraPin ?? ''}</span>
                </Cell>
                <Cell>
                  <Tag state={s.status === 'active' ? 'active' : 'neutral'}>
                    {s.status === 'active' ? 'Active' : 'Inactive'}
                  </Tag>
                </Cell>
                <Cell>
                  <Button
                    dense
                    variant="quiet"
                    isDisabled={!online}
                    onPress={() => {
                      toggle.mutate(s);
                    }}
                  >
                    {s.status === 'active' ? 'Switch off' : 'Switch on'}
                  </Button>
                </Cell>
              </Row>
            )}
          </TableBody>
        </Table>
      ) : null}
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim() !== '') add.mutate();
        }}
      >
        <div className={styles.row}>
          <TextField label="New supplier name" value={name} onChange={setName} />
          <TextField label="KRA PIN (optional)" value={kraPin} onChange={setKraPin} />
        </div>
        {add.error === null ? null : (
          <Notice tone="error">
            {errorText(add.error, "The supplier wasn't added. Try again.")}
          </Notice>
        )}
        <div className={styles.actions}>
          <Button
            type="submit"
            isDisabled={!online || name.trim() === ''}
            isPending={add.isPending}
            pendingLabel="Adding…"
          >
            Add supplier
          </Button>
        </div>
      </form>
    </Section>
  );
}

/** How a catalogue item is counted: its base unit, packs, expiry and controlled flag. */
function ItemSetup() {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [item, setItem] = useState<StockItemView | null>(null);
  const [unitCode, setUnitCode] = useState('');
  const [unitDisplay, setUnitDisplay] = useState('');
  const [expiryTracked, setExpiryTracked] = useState(true);
  const [isControlled, setIsControlled] = useState(false);
  const [packDisplay, setPackDisplay] = useState('');
  const [packSize, setPackSize] = useState('');
  const size = readQuantity(packSize);
  const code = unitCode.trim().toLowerCase().replace(/\s+/g, '_');
  const save = useMutation({
    mutationFn: () =>
      setStockDetails(item?.itemId ?? '', {
        baseUnitCode: code,
        baseUnitDisplay: unitDisplay.trim(),
        expiryTracked,
        isControlled,
        packUnits: [
          ...(item?.stock?.packUnits.filter((p) => p.status === 'active') ?? []).map((p) => ({
            code: p.code,
            display: p.display,
            baseUnitsPerPack: p.baseUnitsPerPack,
          })),
          ...(packDisplay.trim() !== '' && size !== null && size > 0
            ? [{ code: `pack_${size}`, display: packDisplay.trim(), baseUnitsPerPack: size }]
            : []),
        ],
      }),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ['stock-item-search'] });
      toasts.show({ title: 'Stock details saved', description: saved.name });
      setItem(saved);
      setPackDisplay('');
      setPackSize('');
    },
  });
  return (
    <Section title="How items are counted">
      <p className={styles.meta}>
        Stock is counted in whole base units: the smallest unit you ever issue, such as a tablet or
        a mL. The base unit can't change once the item has stock movements.
      </p>
      <StockItemPickerForSetup
        onSelect={(chosen) => {
          setItem(chosen);
          setUnitCode(chosen.stock?.baseUnit.code ?? '');
          setUnitDisplay(chosen.stock?.baseUnit.display ?? '');
          setExpiryTracked(chosen.stock?.expiryTracked ?? true);
          setIsControlled(chosen.stock?.isControlled ?? false);
        }}
      />
      {item === null ? null : (
        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();
            if (code !== '' && unitDisplay.trim() !== '') save.mutate();
          }}
        >
          <p className={styles.lineHeading}>{item.name}</p>
          <div className={styles.row}>
            <TextField
              label="Base unit code"
              description="Like tablet or ml"
              value={unitCode}
              onChange={setUnitCode}
            />
            <TextField label="Base unit, as shown" value={unitDisplay} onChange={setUnitDisplay} />
          </div>
          <Checkbox isSelected={expiryTracked} onChange={setExpiryTracked}>
            Batches have an expiry date
          </Checkbox>
          <Checkbox isSelected={isControlled} onChange={setIsControlled}>
            Controlled drug
          </Checkbox>
          {item.stock?.packUnits.length ? (
            <p className={styles.meta}>
              Packs:{' '}
              {item.stock.packUnits
                .filter((p) => p.status === 'active')
                .map((p) => `${p.display} (${p.baseUnitsPerPack})`)
                .join(', ')}
            </p>
          ) : null}
          <div className={styles.row}>
            <TextField
              label="Add a pack (optional)"
              description="Like Box of 100"
              value={packDisplay}
              onChange={setPackDisplay}
            />
            <TextField
              label="Base units in the pack"
              inputMode="numeric"
              value={packSize}
              onChange={setPackSize}
            />
          </div>
          {save.error === null ? null : (
            <Notice tone="error">
              {errorText(save.error, "The stock details weren't saved. Try again.")}
            </Notice>
          )}
          <div className={styles.actions}>
            <Button
              type="submit"
              isDisabled={!online}
              isPending={save.isPending}
              pendingLabel="Saving…"
            >
              Save stock details
            </Button>
          </div>
        </form>
      )}
    </Section>
  );
}

/** The item picker, but items not yet set up can be chosen, to set them up. */
function StockItemPickerForSetup({ onSelect }: { onSelect: (item: StockItemView) => void }) {
  return <StockItemPicker label="Item" onSelect={onSelect} allowUnconfigured />;
}
